"use client";

import { useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { parseFilters, toFilterParams, type DirectoryFilters } from "@/components/directory-filter-bar";
import type { LocationSelection } from "@/features/maps";
import { withBasePath } from "@/lib/base-path";

export type ViewMode = "list" | "map";

type DirectorySearch = {
  filters: DirectoryFilters;
  location: LocationSelection | null;
  viewMode: ViewMode;
  setFilters: (filters: DirectoryFilters) => void;
  setLocation: (location: LocationSelection | null) => void;
  setViewMode: (viewMode: ViewMode) => void;
};

type DirectorySearchPatch = {
  filters?: DirectoryFilters;
  location?: LocationSelection | null;
  viewMode?: ViewMode;
};

type DirectorySearchState = {
  filters: DirectoryFilters;
  location: LocationSelection | null;
  viewMode: ViewMode;
  /** The query string this state was last written to, to spot outside changes. */
  writtenSearch: string;
};

const NEAR_PARAM = "near";
const LAT_PARAM = "lat";
const LNG_PARAM = "lng";
const VIEW_PARAM = "view";

const coordinate = (value: string | null, limit: number): number | null => {
  if (value === null || value.trim().length === 0) return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) && Math.abs(parsed) <= limit ? Math.round(parsed * 1e6) / 1e6 : null;
};

const parseLocation = (params: URLSearchParams): LocationSelection | null => {
  const address = params.get(NEAR_PARAM)?.trim();
  const lat = coordinate(params.get(LAT_PARAM), 90);
  const lng = coordinate(params.get(LNG_PARAM), 180);

  return address && lat !== null && lng !== null ? { address, coordinates: { lat, lng } } : null;
};

const toSearch = (filters: DirectoryFilters, location: LocationSelection | null, viewMode: ViewMode): string => {
  const params = new URLSearchParams();

  for (const [key, value] of toFilterParams(filters)) params.set(key, value);

  if (location) {
    params.set(NEAR_PARAM, location.address);
    params.set(LAT_PARAM, String(Math.round(location.coordinates.lat * 1e6) / 1e6));
    params.set(LNG_PARAM, String(Math.round(location.coordinates.lng * 1e6) / 1e6));
  }

  if (viewMode === "map") params.set(VIEW_PARAM, viewMode);

  return params.toString();
};

const fromUrl = (params: URLSearchParams, writtenSearch: string): DirectorySearchState => ({
  filters: parseFilters(params),
  location: parseLocation(params),
  viewMode: params.get(VIEW_PARAM) === "map" ? "map" : "list",
  writtenSearch,
});

export const useDirectorySearch = (): DirectorySearch => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  /* Owned by React, mirrored to the URL. Deriving straight from `useSearchParams` re-renders
     only when Next's router store notifies, which is not guaranteed to land in the same batch
     as the change that caused it: invisible for a click on a select, very visible for a change
     made from a network response, where the AI search's filters reached the URL but left the
     controls showing the previous values. */
  const urlSearch = searchParams.toString();
  const [state, setState] = useState<DirectorySearchState>(() => fromUrl(searchParams, urlSearch));

  /* ...but the URL is still an input as well as an output: a shared link, the back button, or a
     first render that happened before the params hydrated all change it behind our back. Adopt
     any query string we did not write ourselves. Setting state during render is the supported
     way to do this — React discards the render and redoes it, with no extra commit. */
  if (urlSearch !== state.writtenSearch) {
    setState(fromUrl(searchParams, urlSearch));
  }

  const commit = (patch: DirectorySearchPatch) => {
    const filters = patch.filters ?? state.filters;
    const location = patch.location !== undefined ? patch.location : state.location;
    const viewMode = patch.viewMode ?? state.viewMode;
    const search = toSearch(filters, location, viewMode);

    setState({ filters, location, viewMode, writtenSearch: search });

    const path = withBasePath(pathname);
    window.history.replaceState(null, "", search ? `${path}?${search}` : path);
  };

  return {
    filters: state.filters,
    location: state.location,
    viewMode: state.viewMode,
    setFilters: (next) => commit({ filters: next }),
    setLocation: (next) => commit({ location: next }),
    setViewMode: (next) => commit({ viewMode: next }),
  };
};
